import React, { useState, useEffect, useCallback } from "react";
import { FileUploader } from "react-drag-drop-files";
import DeleteIcon from '@mui/icons-material/Delete';
import styles from './FileUpload.module.css';

const fileTypes = ["JPEG", "PNG", "PDF", "JPG"];
const supportedPastedTypes = new Set(['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']);

function FileUpload({ onSubmit, initialValues, showPreviews = true, compact = false }) {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const uploaderLabel = 'Drag & drop, browse or paste files';

  useEffect(() => {
    setFiles(Array.isArray(initialValues) ? initialValues : []);
  }, [initialValues]);

  const resolveMimeType = (fileLike) => {
    if (typeof fileLike?.type === 'string' && fileLike.type.trim() !== '') {
      return fileLike.type.toLowerCase();
    }
    const name = String(fileLike?.name || '').toLowerCase();
    if (name.endsWith('.pdf')) return 'application/pdf';
    if (name.endsWith('.png')) return 'image/png';
    if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
    return '';
  };

  const extractBlob = (fileLike) => {
    if (typeof Blob !== 'undefined' && fileLike instanceof Blob) {
      return fileLike;
    }
    if (typeof Blob !== 'undefined' && fileLike?.file instanceof Blob) {
      return fileLike.file;
    }
    if (typeof Blob !== 'undefined' && fileLike?.originFileObj instanceof Blob) {
      return fileLike.originFileObj;
    }
    return null;
  };

  const normalizeIncomingFiles = (incomingFiles) => {
    if (!incomingFiles) return [];
    if (Array.isArray(incomingFiles)) return incomingFiles.filter(Boolean);
    if (typeof FileList !== 'undefined' && incomingFiles instanceof FileList) {
      return Array.from(incomingFiles).filter(Boolean);
    }
    if (typeof incomingFiles?.length === 'number' && typeof incomingFiles?.item === 'function') {
      return Array.from(incomingFiles).filter(Boolean);
    }
    if (incomingFiles?.file || incomingFiles?.originFileObj) {
      return [incomingFiles];
    }
    return [incomingFiles].filter(Boolean);
  };

  const toDataUrl = (rawData, mimeType) => {
    if (typeof rawData !== 'string') return '';
    const trimmed = rawData.trim();
    if (trimmed === '') return '';
    if (trimmed.startsWith('data:')) return trimmed;
    return `data:${mimeType || 'application/octet-stream'};base64,${trimmed}`;
  };

  const appendFiles = useCallback((incomingFiles) => {
    const normalizedFiles = normalizeIncomingFiles(incomingFiles);
    if (normalizedFiles.length === 0) return;

    setFiles((prevFiles) => {
      const updatedFiles = [...prevFiles, ...normalizedFiles];
      onSubmit(updatedFiles);
      return updatedFiles;
    });
  }, [onSubmit]);

  const handleChange = (newFiles) => {
    appendFiles(newFiles);
  };

  const handlePaste = useCallback((event) => {
    const clipboardItems = Array.from(event.clipboardData?.items || []);
    if (!clipboardItems.length) return;

    const pastedFiles = clipboardItems
      .filter((item) => item.kind === 'file')
      .map((item) => item.getAsFile())
      .filter(Boolean);

    if (pastedFiles.length === 0) return;

    const unsupportedFile = pastedFiles.find((file) => !supportedPastedTypes.has(file.type));
    if (unsupportedFile) {
      alert('Unsupported pasted file type. Please paste PNG/JPG images or upload a PDF.');
      return;
    }

    event.preventDefault();
    const timestamp = Date.now();
    const normalizedFiles = pastedFiles.map((file, index) => {
      if (file.name && file.name.trim() !== '') {
        return file;
      }

      const extension = file.type === 'application/pdf'
        ? 'pdf'
        : (file.type === 'image/png' ? 'png' : 'jpg');

      return new File([file], `pasted-file-${timestamp}-${index + 1}.${extension}`, {
        type: file.type,
        lastModified: timestamp,
      });
    });

    appendFiles(normalizedFiles);
  }, [appendFiles]);

  useEffect(() => {
    const createdObjectUrls = [];
    const nextPreviews = files.map((file) => {
      const blob = extractBlob(file);
      const mimeType = resolveMimeType(blob || file);
      const hasPreviewType = mimeType.startsWith('image/') || mimeType === 'application/pdf';
      if (!hasPreviewType) {
        return null;
      }

      if (typeof file?.url === 'string' && file.url.trim() !== '') {
        return { src: file.url, mimeType };
      }

      if (typeof file?.data === 'string' && file.data.trim() !== '') {
        return { src: toDataUrl(file.data, mimeType), mimeType };
      }

      if (blob) {
        const objectUrl = URL.createObjectURL(blob);
        createdObjectUrls.push(objectUrl);
        return { src: objectUrl, mimeType };
      }

      if (typeof file?.base64 === 'string' && file.base64.trim() !== '') {
        return { src: toDataUrl(file.base64, mimeType), mimeType };
      }

      return null;
    });

    setPreviews(nextPreviews);

    return () => {
      createdObjectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [files]);

  useEffect(() => {
    const handleDocumentPaste = (event) => {
      const target = event.target;
      const tagName = target?.tagName?.toLowerCase();
      const isTextInput = tagName === 'input' || tagName === 'textarea' || target?.isContentEditable;

      if (isTextInput) return;
      handlePaste(event);
    };

    document.addEventListener('paste', handleDocumentPaste);
    return () => document.removeEventListener('paste', handleDocumentPaste);
  }, [handlePaste]);

  const handleDelete = (index) => {
    setFiles((prevFiles) => {
      const updatedFiles = [...prevFiles];
      updatedFiles.splice(index, 1);
      onSubmit(updatedFiles);
      return updatedFiles;
    });
  };


  return (
    <div className={`${styles.container} ${compact ? styles.compact : ''}`}>
      <FileUploader
        handleChange={handleChange}
        name="file"
        types={fileTypes}
        multiple={true}
        label={uploaderLabel}
        fileOrFiles={files.length > 0 ? files : null}
      />

      {showPreviews && files.length > 0 && (
        <div className={styles.previewSection}>
          <h3 className={styles.previewTitle} style={{ color: 'var(--theme-text)' }}>Uploaded Files</h3>
          <div className={styles.previewList}>
            {files.map((file, index) => (
              <div key={index} className={styles.uploadedFileRow}>
                {previews[index]?.mimeType?.startsWith('image/') ? (
                  <img
                    src={previews[index].src}
                    alt="Uploaded invoice preview"
                    className={styles.uploadedImage}
                  />
                ) : previews[index]?.mimeType === 'application/pdf' ? (
                  <object
                    data={previews[index].src}
                    type="application/pdf"
                    className={styles.uploadedPdf}
                    aria-label="Uploaded PDF preview"
                  >
                    <div className={styles.previewFallback}>PDF preview unavailable</div>
                  </object>
                ) : (
                  <div className={styles.previewFallback}>Preview unavailable</div>
                )}
                <DeleteIcon fontSize="large" className={styles.deleteIcon} onClick={() => handleDelete(index)}></DeleteIcon>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default FileUpload;
